export interface RelatedTask {
  id: number;
  title: string;
  projectId: number | null;
  assigneeId: number | null;
}
export interface Membership {
  projectId: number;
  userId: number;
  role: 'manager' | 'member';
}
export const relationshipUsers = [
  { id: 1, name: 'Maya' },
  { id: 2, name: 'Ravi' },
  { id: 3, name: 'Jo' },
];
export const relationshipProjects = [
  { id: 1, name: 'Apollo' },
  { id: 2, name: 'Support' },
];
export const relationshipMemberships: Membership[] = [
  { projectId: 1, userId: 1, role: 'manager' },
  { projectId: 1, userId: 2, role: 'member' },
  { projectId: 2, userId: 2, role: 'manager' },
];
// Tasks 1, 4 and 8 are older tasks created before projects existed, like the imported workshop tasks.
export const relationshipTasks = (): RelatedTask[] => [
  { id: 1, title: 'Prepare the demo', projectId: null, assigneeId: null },
  { id: 4, title: "Read Maya's notes", projectId: null, assigneeId: null },
  { id: 8, title: 'Check the examples', projectId: null, assigneeId: null },
  { id: 9, title: 'Write the release note', projectId: 1, assigneeId: 2 },
  { id: 10, title: 'Check the dates', projectId: 1, assigneeId: null },
];
export type FactDesign = 'copies' | 'reference';
export function renameProject(design: FactDesign, renamed: boolean) {
  return {
    projectName: renamed ? 'Apollo Demo' : 'Apollo',
    taskNames: [
      renamed ? 'Apollo Demo' : 'Apollo',
      renamed && design === 'reference' ? 'Apollo Demo' : 'Apollo',
    ],
    inconsistent: renamed && design === 'copies',
  };
}

export type MembershipKey = 'user' | 'pair';
export function addMembership(
  rows: Membership[],
  projectId: number,
  userId: number,
  key: MembershipKey,
) {
  if (
    !relationshipProjects.some((project) => project.id === projectId) ||
    !relationshipUsers.some((user) => user.id === userId)
  )
    return { rows, accepted: false, message: 'The project and user must both exist.' };
  const conflict = rows.some(
    (row) => row.userId === userId && (key === 'user' || row.projectId === projectId),
  );
  if (conflict)
    return {
      rows,
      accepted: false,
      message:
        key === 'user'
          ? 'Rejected: user_id alone allows Maya to belong to only one project. The key is too restrictive.'
          : 'Rejected: this project/user pair already exists. One membership per pair is enough.',
    };
  return {
    rows: [...rows, { projectId, userId, role: 'member' as const }],
    accepted: true,
    message:
      'Added: the same person can belong to two different projects. Neither the person nor the project was copied.',
  };
}

export type JoinKind = 'inner' | 'left';
export type JoinPath = 'assignee' | 'members';
export function joinRelationships(kind: JoinKind, path: JoinPath) {
  return relationshipTasks().flatMap((task) => {
    const project = relationshipProjects.find((row) => row.id === task.projectId);
    const users =
      path === 'assignee'
        ? relationshipUsers.filter((row) => row.id === task.assigneeId)
        : relationshipMemberships
            .filter((row) => row.projectId === task.projectId)
            .flatMap((membership) =>
              relationshipUsers.filter((row) => row.id === membership.userId),
            );
    if (kind === 'inner' && (!project || users.length === 0)) return [];
    return (users.length ? users : [null]).map((user) => ({
      taskId: task.id,
      title: task.title,
      project: project?.name ?? null,
      person: user?.name ?? null,
    }));
  });
}
export function relationshipJoinSql(kind: JoinKind, path: JoinPath) {
  const join = kind === 'left' ? 'LEFT JOIN' : 'INNER JOIN';
  return `SELECT t.id, t.title, p.name AS project, u.name AS person\nFROM tasks AS t\n${join} projects AS p ON p.id = t.project_id\n${
    path === 'assignee'
      ? `${join} users AS u ON u.id = t.assignee_id`
      : `${join} memberships AS m ON m.project_id = t.project_id\n${join} users AS u ON u.id = m.user_id`
  }\nORDER BY t.id, u.id;`;
}

export type AssignmentExample =
  'member' | 'other-project' | 'project-only' | 'personal' | 'assignee-only' | 'missing-project';
export type AssignmentRule = 'separate' | 'membership';
export function assignmentValues(
  example: AssignmentExample,
): Pick<RelatedTask, 'projectId' | 'assigneeId'> {
  return {
    member: { projectId: 1, assigneeId: 2 },
    'other-project': { projectId: 1, assigneeId: 3 },
    'project-only': { projectId: 1, assigneeId: null },
    personal: { projectId: null, assigneeId: null },
    'assignee-only': { projectId: null, assigneeId: 2 },
    'missing-project': { projectId: 999, assigneeId: null },
  }[example];
}
export function inspectAssignment(
  values: Pick<RelatedTask, 'projectId' | 'assigneeId'>,
  rule: AssignmentRule,
) {
  if (values.assigneeId !== null && values.projectId === null)
    return {
      accepted: false,
      message: 'Rejected by CHECK: an assigned task must also have a project.',
    };
  if (
    values.projectId !== null &&
    !relationshipProjects.some((project) => project.id === values.projectId)
  )
    return {
      accepted: false,
      message:
        'Rejected by the project foreign key: project #999 does not exist. This still matters when assignee_id is NULL.',
    };
  if (
    values.assigneeId !== null &&
    !relationshipUsers.some((user) => user.id === values.assigneeId)
  )
    return { accepted: false, message: 'Rejected: the user does not exist.' };
  if (
    rule === 'membership' &&
    values.assigneeId !== null &&
    !relationshipMemberships.some(
      (row) => row.projectId === values.projectId && row.userId === values.assigneeId,
    )
  )
    return {
      accepted: false,
      message: 'Rejected by the pair foreign key: Jo exists, but has no membership in Apollo.',
    };
  return {
    accepted: true,
    message:
      values.assigneeId === null
        ? values.projectId === null
          ? 'Accepted: an ungrouped task may have no project and no assignee.'
          : 'Accepted: a task may belong to a project before someone is assigned.'
        : rule === 'separate' &&
            !relationshipMemberships.some(
              (row) => row.projectId === values.projectId && row.userId === values.assigneeId,
            )
          ? 'Accepted by separate foreign keys, but wrong for our rule: Jo has no membership in Apollo.'
          : 'Accepted: the assignee is a member of this task’s project.',
  };
}
export function constraintAttempt(layer: 'python' | 'database', entry: 'api' | 'sql') {
  const checked = layer === 'database' || entry === 'api';
  return {
    accepted: !checked,
    message: checked
      ? entry === 'api' && layer === 'python'
        ? 'The API rejects the assignment. Now try the same write through a direct database connection.'
        : 'The database rejects the assignment on either path. Task #10 stays unassigned.'
      : 'The direct write bypasses the Python check. The database accepts Jo for Apollo even though Jo is not a member.',
  };
}

export interface MigrationState {
  relationships: boolean;
  history: number[];
}
export const initialMigration = (): MigrationState => ({ relationships: false, history: [] });
export function applyRelationshipMigration(state: MigrationState, atomic: boolean, fail: boolean) {
  if (state.history.includes(2))
    return {
      state,
      outcome: 'skipped' as const,
      message: 'Version 2 is recorded. Skip it: no tables, columns or old tasks change.',
    };
  if (state.relationships)
    return {
      state,
      outcome: 'failed' as const,
      message:
        'The columns already exist but version 2 is absent. Repeating ALTER TABLE fails. Separate commits left an incomplete migration.',
    };
  if (fail)
    return {
      state: atomic ? state : { relationships: true, history: [] },
      outcome: 'failed' as const,
      message: atomic
        ? 'A later statement failed. Rollback removed the new tables and columns, and version 2 was not recorded. Existing tasks are unchanged.'
        : 'A later statement failed after the schema was committed. New tables and columns remain, but version 2 is absent. Existing tasks remain too.',
    };
  return {
    state: { relationships: true, history: [2] },
    outcome: 'applied' as const,
    message: atomic
      ? 'Committed schema changes and version 2 together. Existing task IDs, titles and completion states remain; new relationship columns start as NULL.'
      : 'Committed the schema, then recorded version 2. Both steps happened to succeed this time. Existing tasks remain, with NULL in the new columns.',
  };
}

export interface ProjectCreationState {
  phase: 'ready' | 'project-added' | 'manager-added' | 'failed' | 'done';
  storedProject: boolean;
  storedMembership: boolean;
  pendingProject: boolean;
  pendingMembership: boolean;
  message: string;
}
export const initialProjectCreation = (): ProjectCreationState => ({
  phase: 'ready',
  storedProject: false,
  storedMembership: false,
  pendingProject: false,
  pendingMembership: false,
  message:
    'Create a project and its first manager. Both records are needed for the operation to succeed.',
});
export function projectCreationStep(
  state: ProjectCreationState,
  atomic: boolean,
  managerExists: boolean,
): ProjectCreationState {
  if (state.phase === 'ready')
    return {
      ...state,
      phase: 'project-added',
      storedProject: !atomic,
      pendingProject: atomic,
      message: atomic
        ? 'The project is pending in the transaction. Other connections cannot see it yet.'
        : 'The project was committed separately. Other connections can already see it.',
    };
  if (state.phase === 'project-added')
    return managerExists
      ? {
          ...state,
          phase: 'manager-added',
          storedMembership: !atomic,
          pendingMembership: atomic,
          message: atomic
            ? 'The manager membership is pending in the same transaction.'
            : 'The manager membership was committed separately.',
        }
      : {
          ...state,
          phase: 'failed',
          pendingProject: false,
          pendingMembership: false,
          message: atomic
            ? 'The manager user is missing. Rollback leaves neither the project nor its membership committed.'
            : 'The manager user is missing. Its insert fails, but the already committed project remains without a manager.',
        };
  if (state.phase === 'manager-added')
    return {
      ...state,
      phase: 'done',
      storedProject: true,
      storedMembership: true,
      pendingProject: false,
      pendingMembership: false,
      message: atomic
        ? 'Commit made the project and manager membership visible together.'
        : 'Both records now exist, but the earlier partial state was visible between commits.',
    };
  return state;
}

export interface MembershipRemovalState {
  assigned: boolean;
  membership: boolean;
  message: string;
}
export const initialMembershipRemoval = (): MembershipRemovalState => ({
  assigned: true,
  membership: true,
  message:
    'Task #9 is assigned to Ravi in Apollo. Ravi’s membership is still referenced by that task.',
});
export function membershipRemoval(
  state: MembershipRemovalState,
  action: 'remove' | 'unassign',
): MembershipRemovalState {
  if (action === 'unassign')
    return {
      ...state,
      assigned: false,
      message: 'Set assignee_id to NULL. The task, its project and Ravi’s user record all remain.',
    };
  if (state.assigned)
    return {
      ...state,
      message:
        'RESTRICT rejects removal: task #9 still points to this project/user membership. Nothing was deleted.',
    };
  return {
    ...state,
    membership: false,
    message:
      'Membership removed. Task #9 stays in Apollo, unassigned. Ravi’s user record remains available for other projects.',
  };
}
